/**
 * Structural tree transforms — the "heavy" restructuring ops beyond per-prop
 * edits: GROUP a set of sibling blocks into a new container, UNGROUP a
 * container back into its parent, and MOVE a block into a different parent
 * (re-parent). All are PURE (return a new tree or the input unchanged), never
 * throw, and live here (registry-free) so they're independently testable and
 * reusable by studio-ui's op pipeline and, later, MCP/AI.
 *
 * They deliberately do NOT know the component registry — validation (acceptsChildren,
 * known component) happens in the caller (studio-ui `validateProposalOp`), which
 * has the registry. These functions just move nodes around safely.
 */
import type { ComponentNode, ComponentTree } from "./tree.js";

/* -------------------------------- helpers --------------------------------- */

function findNode(node: ComponentNode, id: string): ComponentNode | null {
  if (node.node_id === id) return node;
  for (const c of node.children ?? []) {
    const hit = findNode(c, id);
    if (hit) return hit;
  }
  return null;
}

/** node_id of the parent that holds `id` as a direct child, or null. */
function parentIdOf(root: ComponentNode, id: string): string | null {
  for (const c of root.children ?? []) {
    if (c.node_id === id) return root.node_id;
    const hit = parentIdOf(c, id);
    if (hit) return hit;
  }
  return null;
}

/** Strip grid placement props (meaningless once a block leaves a grid). */
function stripPlacement(node: ComponentNode): ComponentNode {
  if (node.props == null || (!("colSpan" in node.props) && !("rowSpan" in node.props))) {
    return node;
  }
  const props = { ...node.props };
  delete props.colSpan;
  delete props.rowSpan;
  return { ...node, props };
}

/** Return a new root with `id` removed wherever it sits (root itself is never removed). */
function removeFrom(node: ComponentNode, id: string): ComponentNode {
  if (!node.children || node.children.length === 0) return node;
  const kept = node.children.filter((c) => c.node_id !== id);
  let changed = kept.length !== node.children.length;
  const next = kept.map((c) => {
    const u = removeFrom(c, id);
    if (u !== c) changed = true;
    return u;
  });
  return changed ? { ...node, children: next } : node;
}

/* --------------------------------- group ---------------------------------- */

/**
 * Wrap `nodeIds` (which MUST be direct siblings of one parent) into a new
 * container node, inserted where the earliest wrapped sibling was. The wrapped
 * nodes keep their order and become the container's children.
 */
export function wrapNodes(
  tree: ComponentTree,
  nodeIds: string[],
  container: { name: string; version: string; props?: Record<string, unknown> },
  newNodeId: string,
): ComponentTree {
  try {
    if (!tree?.root || nodeIds.length === 0) return tree;
    const ids = new Set(nodeIds);
    if (ids.has(tree.root.node_id)) return tree; // can't wrap the root

    // The wrap is valid only if ALL ids are direct children of ONE parent.
    const parents = new Set(nodeIds.map((id) => parentIdOf(tree.root, id)));
    if (parents.size !== 1) return tree;
    const parentId = [...parents][0];
    if (!parentId) return tree;

    const rebuild = (n: ComponentNode): ComponentNode => {
      if (n.node_id === parentId) {
        const kids = n.children ?? [];
        const wrapped = kids.filter((k) => ids.has(k.node_id)); // original order
        if (wrapped.length !== ids.size) return n; // a child went missing — bail
        const wrapper: ComponentNode = {
          node_id: newNodeId,
          component: { name: container.name, version: container.version },
          props: { ...(container.props ?? {}) },
          children: wrapped,
        };
        const newKids: ComponentNode[] = [];
        let inserted = false;
        for (const k of kids) {
          if (ids.has(k.node_id)) {
            if (!inserted) {
              newKids.push(wrapper);
              inserted = true;
            }
          } else {
            newKids.push(k);
          }
        }
        return { ...n, children: newKids };
      }
      if (!n.children || n.children.length === 0) return n;
      let changed = false;
      const next = n.children.map((c) => {
        const u = rebuild(c);
        if (u !== c) changed = true;
        return u;
      });
      return changed ? { ...n, children: next } : n;
    };

    const out = rebuild(tree.root);
    return out === tree.root ? tree : { root: out };
  } catch {
    return tree;
  }
}

/* -------------------------------- ungroup --------------------------------- */

/**
 * Unwrap a container: promote its children into its parent at its position
 * (placement props stripped), then drop the container. Root cannot be unwrapped;
 * a childless container is simply removed.
 */
export function unwrapNode(tree: ComponentTree, nodeId: string): ComponentTree {
  try {
    if (!tree?.root || nodeId === tree.root.node_id) return tree;

    const rebuild = (n: ComponentNode): ComponentNode => {
      const kids = n.children ?? [];
      const idx = kids.findIndex((k) => k.node_id === nodeId);
      if (idx >= 0) {
        const target = kids[idx]!;
        const promoted = (target.children ?? []).map(stripPlacement);
        const newKids = [...kids.slice(0, idx), ...promoted, ...kids.slice(idx + 1)];
        return { ...n, children: newKids };
      }
      if (kids.length === 0) return n;
      let changed = false;
      const next = kids.map((c) => {
        const u = rebuild(c);
        if (u !== c) changed = true;
        return u;
      });
      return changed ? { ...n, children: next } : n;
    };

    const out = rebuild(tree.root);
    return out === tree.root ? tree : { root: out };
  } catch {
    return tree;
  }
}

/* ------------------------------- re-parent -------------------------------- */

/**
 * Move `nodeId` to become a child of `toParentId` at `index` (default: append).
 * Refuses to move the root, into itself, or into its own descendant (cycle).
 */
export function moveNodeToParent(
  tree: ComponentTree,
  nodeId: string,
  toParentId: string,
  index?: number,
): ComponentTree {
  try {
    if (!tree?.root || nodeId === tree.root.node_id || nodeId === toParentId) return tree;
    const node = findNode(tree.root, nodeId);
    const target = findNode(tree.root, toParentId);
    if (!node || !target) return tree;
    // Cycle guard: target must not live inside the moving node's subtree.
    if (findNode(node, toParentId)) return tree;
    // No-op if it's already a child of the target at the same effective slot.
    const curParent = parentIdOf(tree.root, nodeId);
    if (curParent === toParentId && index === undefined) {
      // already there and no explicit reorder requested
      const curKids = target.children ?? [];
      if (curKids.length > 0 && curKids[curKids.length - 1]!.node_id === nodeId) return tree;
    }

    const withoutNode = removeFrom(tree.root, nodeId);

    const insert = (n: ComponentNode): ComponentNode => {
      if (n.node_id === toParentId) {
        const kids = n.children ?? [];
        const at = index === undefined ? kids.length : Math.min(Math.max(0, index), kids.length);
        const moved = stripPlacement(node); // spans are parent-specific
        return { ...n, children: [...kids.slice(0, at), moved, ...kids.slice(at)] };
      }
      if (!n.children || n.children.length === 0) return n;
      let changed = false;
      const next = n.children.map((c) => {
        const u = insert(c);
        if (u !== c) changed = true;
        return u;
      });
      return changed ? { ...n, children: next } : n;
    };

    return { root: insert(withoutNode) };
  } catch {
    return tree;
  }
}
